"use client"

import { useRef, useState } from "react"
import { Send, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { PhoneInput } from "@/components/ui/phone-input"
import type { CountryCode } from "@/lib/phone"
import { UF_OPTIONS } from "@/lib/constants"
import { useTranslations } from "@/lib/i18n/client"
import { TurnstileWidget, type TurnstileHandle } from "./TurnstileWidget"

interface ExtraField {
  id: string
  label: string
  placeholder?: string
}

interface ContactFormIds {
  nome: string
  email: string
  telefone: string
  cidade: string
  uf: string
  mensagem: string
}

// ---------------------------------------------------------------------------
// Base compartilhada — usada por ContactForm (página de contato) e
// AeronaveContactForm (página de detalhe). Campos, Turnstile, submit e
// tratamento de erro vivem só aqui.
// ---------------------------------------------------------------------------

interface ContactFormBaseProps {
  domainKey: string
  defaultCountry?: CountryCode
  /** ids DOM dos campos (preservados por compatibilidade entre os dois forms) */
  ids: ContactFormIds
  namePlaceholder: string
  phoneLabel: string
  /** grid da linha cidade/UF (largura da coluna UF difere entre os forms) */
  ufGridClassName: string
  ufTriggerClassName?: string
  extraFields?: ExtraField[]
  messagePlaceholder: string
  messageRows: number
  /** Prefixo aplicado à mensagem final (ex: "[Financiamento]\n") */
  assuntoPrefix?: string
  /** Fallback quando a mensagem fica vazia (detalhe usa o placeholder) */
  emptyMessageFallback?: string
  /** Payload extra no POST /api/contato (ex: aeronave_id) */
  extraPayload?: Record<string, unknown>
  successMessage: string
  submitLabel: string
  formClassName?: string
}

export function ContactFormBase({
  domainKey,
  defaultCountry = "BR",
  ids,
  namePlaceholder,
  phoneLabel,
  ufGridClassName,
  ufTriggerClassName = "mt-1.5",
  extraFields,
  messagePlaceholder,
  messageRows,
  assuntoPrefix,
  emptyMessageFallback,
  extraPayload,
  successMessage,
  submitLabel,
  formClassName = "space-y-4",
}: ContactFormBaseProps) {
  const { t, locale } = useTranslations()
  const [nome, setNome] = useState("")
  const [email, setEmail] = useState("")
  const [telefone, setTelefone] = useState("")
  const [cidade, setCidade] = useState("")
  const [uf, setUf] = useState("")
  const [mensagem, setMensagem] = useState("")
  const [extras, setExtras] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstileRef = useRef<TurnstileHandle>(null)

  function buildMensagem(): string {
    const parts: string[] = []

    if (extraFields) {
      for (const field of extraFields) {
        const val = extras[field.id]?.trim()
        if (val) parts.push(`${field.label}: ${val}`)
      }
    }

    if (mensagem.trim()) parts.push(mensagem.trim())

    const body = parts.join("\n") || emptyMessageFallback || ""
    return assuntoPrefix ? `${assuntoPrefix}${body}` : body
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const newErrors: Record<string, string> = {}
    if (!nome.trim()) newErrors.nome = t.form.nameError

    const finalMsg = buildMensagem()
    if (!finalMsg || finalMsg === assuntoPrefix) {
      newErrors.mensagem = t.form.messageError
    }

    if (!turnstileToken) {
      newErrors.captcha = t.form.captchaRequired
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})
    setLoading(true)

    try {
      const res = await fetch("/api/contato", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...extraPayload,
          nome: nome.trim(),
          email: email.trim() || undefined,
          telefone: telefone.trim() || undefined,
          cidade: cidade.trim() || undefined,
          uf: uf || undefined,
          mensagem: finalMsg,
          origem: domainKey,
          turnstileToken,
        }),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || t.form.sendError)
      }

      toast.success(successMessage)
      setNome("")
      setEmail("")
      setTelefone("")
      setCidade("")
      setUf("")
      setMensagem("")
      setExtras({})
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t.form.sendError,
      )
    } finally {
      setLoading(false)
      turnstileRef.current?.reset()
      setTurnstileToken(null)
    }
  }

  return (
    <form onSubmit={handleSubmit} className={formClassName}>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor={ids.nome}>{t.form.nameRequired}</Label>
          <Input
            id={ids.nome}
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder={namePlaceholder}
            className="mt-1.5 uppercase"
            aria-invalid={!!errors.nome}
            aria-describedby={errors.nome ? `${ids.nome}-error` : undefined}
          />
          {errors.nome && (
            <p id={`${ids.nome}-error`} role="alert" className="mt-1 text-xs text-red-600">{errors.nome}</p>
          )}
        </div>
        <div>
          <Label htmlFor={ids.email}>{t.form.email}</Label>
          <Input
            id={ids.email}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.form.emailPlaceholder}
            className="mt-1.5"
          />
        </div>
      </div>

      <div>
        <Label htmlFor={ids.telefone}>{phoneLabel}</Label>
        <PhoneInput
          id={ids.telefone}
          value={telefone}
          onChange={setTelefone}
          defaultCountry={defaultCountry}
          nameLocale={locale}
          labels={{
            countrySelect: t.form.phoneCountry,
            countrySearch: t.form.phoneCountrySearch,
            countryEmpty: t.form.phoneCountryEmpty,
          }}
          className="mt-1.5"
        />
      </div>

      <div className={`grid gap-4 ${ufGridClassName}`}>
        <div>
          <Label htmlFor={ids.cidade}>{t.form.city}</Label>
          <Input
            id={ids.cidade}
            value={cidade}
            onChange={(e) => setCidade(e.target.value)}
            placeholder={t.form.cityPlaceholder}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor={ids.uf}>{t.form.state}</Label>
          <Select value={uf} onValueChange={setUf}>
            <SelectTrigger id={ids.uf} className={ufTriggerClassName}>
              <SelectValue placeholder={t.form.state} />
            </SelectTrigger>
            <SelectContent>
              {UF_OPTIONS.map((estado) => (
                <SelectItem key={estado} value={estado}>
                  {estado}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {extraFields?.map((field) => (
        <div key={field.id}>
          <Label htmlFor={`cf-${field.id}`}>{field.label}</Label>
          <Input
            id={`cf-${field.id}`}
            value={extras[field.id] ?? ""}
            onChange={(e) =>
              setExtras((prev) => ({ ...prev, [field.id]: e.target.value }))
            }
            placeholder={field.placeholder}
            className="mt-1.5"
          />
        </div>
      ))}

      <div>
        <Label htmlFor={ids.mensagem}>{t.form.messageRequired}</Label>
        <Textarea
          id={ids.mensagem}
          value={mensagem}
          onChange={(e) => setMensagem(e.target.value)}
          placeholder={messagePlaceholder}
          rows={messageRows}
          className="mt-1.5"
          aria-invalid={!!errors.mensagem}
          aria-describedby={errors.mensagem ? `${ids.mensagem}-error` : undefined}
        />
        {errors.mensagem && (
          <p id={`${ids.mensagem}-error`} role="alert" className="mt-1 text-xs text-red-600">{errors.mensagem}</p>
        )}
      </div>

      <div>
        <TurnstileWidget
          ref={turnstileRef}
          theme="light"
          onVerify={(token) => {
            setTurnstileToken(token)
            setErrors((prev) => {
              if (!prev.captcha) return prev
              const next = { ...prev }
              delete next.captcha
              return next
            })
          }}
          onExpire={() => setTurnstileToken(null)}
          onError={() => setTurnstileToken(null)}
        />
        {errors.captcha && (
          <p role="alert" className="mt-1 text-xs text-red-600">{errors.captcha}</p>
        )}
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full sm:w-auto"
      >
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Send className="mr-2 h-4 w-4" />
        )}
        {submitLabel}
      </Button>
    </form>
  )
}

// ---------------------------------------------------------------------------
// ContactForm — página de contato (e variantes em sobre/financiamento)
// ---------------------------------------------------------------------------

interface ContactFormProps {
  domainKey: string
  assuntoPrefix?: string
  successMessage?: string
  extraFields?: ExtraField[]
  defaultCountry?: CountryCode
}

export function ContactForm({
  domainKey,
  assuntoPrefix,
  successMessage,
  extraFields,
  defaultCountry = "BR",
}: ContactFormProps) {
  const { t } = useTranslations()

  return (
    <ContactFormBase
      domainKey={domainKey}
      defaultCountry={defaultCountry}
      ids={{
        nome: "cf-nome",
        email: "cf-email",
        telefone: "cf-telefone",
        cidade: "cf-cidade",
        uf: "cf-uf",
        mensagem: "cf-mensagem",
      }}
      namePlaceholder={t.form.namePlaceholder}
      phoneLabel={t.form.phone}
      ufGridClassName="sm:grid-cols-[1fr_100px]"
      extraFields={extraFields}
      messagePlaceholder={t.form.messagePlaceholder}
      messageRows={5}
      assuntoPrefix={assuntoPrefix}
      successMessage={successMessage ?? t.form.sentSuccess}
      submitLabel={t.form.send}
    />
  )
}
